import apiClient from '../../shared/services/apiClient';

/**
 * 订阅管理服务（管理员）
 * 用于管理用户订阅的完整生命周期
 */
export const subscriptionService = {
    // ==========================================
    // 订阅列表和详情
    // ==========================================

    /**
     * 获取订阅列表
     * @param {Object} params - 查询参数
     * @param {number} params.user_id - 用户ID
     * @param {string} params.status - 订阅状态
     * @param {string} params.product_type - 产品类型
     * @returns {Promise} 订阅列表
     */
    getSubscriptions: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/subscriptions/list', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个订阅详情
     * @param {number} id - 订阅ID
     * @returns {Promise} 订阅详细信息
     */
    grantAddon: async (subscriptionId, productId, quantity = 1) => {
        try {
            const r = await apiClient.post('/api/admin/subscriptions/addon-grant', { subscription_id: subscriptionId, product_id: productId, quantity });
            return r.data;
        } catch (e) { return e.response?.data || { success: false, error: e.message }; }
    },

    cancelAddon: async (childId) => {
        try {
            const r = await apiClient.post('/api/admin/subscriptions/addon-cancel', { child_id: childId });
            return r.data;
        } catch (e) { return e.response?.data || { success: false, error: e.message }; }
    },

    recomputeUsage: async (subscriptionId) => {
        try {
            const r = await apiClient.post('/api/admin/subscriptions/usage-recompute', { subscription_id: subscriptionId });
            return r.data;
        } catch (e) { return e.response?.data || { success: false, error: e.message }; }
    },

    getSubscription: async (id) => {
        try {
            const response = await apiClient.get(`/api/admin/subscriptions/get?id=${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 订阅管理操作
    // ==========================================

    /**
     * 创建订阅（管理员为用户创建）
     * @param {Object} data - 订阅数据
     * @param {number} data.user_id - 用户ID
     * @param {number} data.product_id - 产品ID
     * @param {string} data.billing_cycle - 计费周期 (monthly, quarterly, semi_annually, annually)
     * @param {number} data.quantity - 数量（默认1）
     * @returns {Promise} 创建结果
     */
    createSubscription: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 暂停订阅
     * @param {number} id - 订阅ID
     * @param {string} reason - 暂停原因（可选）
     * @returns {Promise} 暂停结果
     */
    suspendSubscription: async (id, reason = null) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/suspend', {
                id,
                reason
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 恢复订阅
     * @param {number} id - 订阅ID
     * @param {string} reason - 恢复原因（可选）
     * @returns {Promise} 恢复结果
     */
    resumeSubscription: async (id, reason = null) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/resume', {
                id,
                reason
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 取消订阅
     * @param {number} id - 订阅ID
     * @param {string} reason - 取消原因（可选）
     * @returns {Promise} 取消结果
     */
    cancelSubscription: async (id, reason = null) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/cancel', {
                id,
                reason
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 终止订阅（立即停止）
     * @param {number} id - 订阅ID
     * @param {string} reason - 终止原因（可选）
     * @returns {Promise} 终止结果
     */
    terminateSubscription: async (id, reason = null) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/terminate', {
                id,
                reason
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新订阅配额（管理员自定义配额）
     * @param {number} id - 订阅ID
     * @param {Object} quotas - 配额设置对象，例如 { dns_records_limit: 100 }
     * @returns {Promise} 更新结果
     */
    updateQuota: async (id, quotas) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/update-quota', {
                id,
                quotas
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新订阅信息
     * @param {number} id - 订阅ID
     * @param {Object} data - 更新数据
     * @param {string} data.billing_cycle - 计费周期
     * @param {boolean} data.auto_renew - 自动续费
     * @param {string} data.expiry_date - 到期日期
     * @param {string} data.next_billing_date - 下次计费日期
     * @param {number} data.quantity - 数量
     * @returns {Promise} 更新结果
     */
    updateSubscription: async (id, data) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/update', { id, ...data });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 续费管理
    // ==========================================

    /**
     * 获取即将到期需要续费的订阅
     * @param {number} days - 提前天数（默认7）
     * @returns {Promise} 待续费订阅列表
     */
    getPendingRenewals: async (days = 7) => {
        try {
            const response = await apiClient.get('/api/admin/subscriptions/renewals/pending', {
                params: { days }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 批量处理所有到期续费（生成发票并尝试自动扣款）
     * @returns {Promise} 处理结果
     */
    processRenewals: async () => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/renewals/process', {});
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 为指定订阅生成续费发票
     * @param {number} subscriptionId - 订阅ID
     * @returns {Promise} 生成结果（含 invoice_id）
     */
    generateRenewal: async (subscriptionId) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/renewal/generate', {
                subscription_id: subscriptionId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 尝试对续费发票自动扣款
     * @param {number} invoiceId - 发票ID
     * @returns {Promise} 扣款结果
     */
    payRenewal: async (invoiceId) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/renewal/pay', {
                invoice_id: invoiceId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 切换订阅自动续费状态
     * @param {number} subscriptionId - 订阅ID
     * @returns {Promise} 切换结果（含 auto_renew 新状态）
     */
    toggleAutoRenew: async (subscriptionId) => {
        try {
            const response = await apiClient.post('/api/admin/subscriptions/toggle-auto-renew', {
                subscription_id: subscriptionId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取可用产品列表（用于创建订阅）
     * @returns {Promise} 产品列表
     */
    getProducts: async () => {
        try {
            const response = await apiClient.get('/api/admin/subscriptions/products');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 常量定义
    // ==========================================

    /**
     * 订阅状态枚举
     */
    STATUS: {
        PENDING: 'pending',       // 待激活
        ACTIVE: 'active',         // 活跃
        SUSPENDED: 'suspended',   // 已暂停
        CANCELLED: 'cancelled',   // 已取消（到期后停止）
        TERMINATED: 'terminated'  // 已终止（立即停止）
    },

    /**
     * 计费周期枚举
     */
    BILLING_CYCLES: {
        MONTHLY: 'monthly',
        QUARTERLY: 'quarterly',
        SEMI_ANNUALLY: 'semi_annually',
        ANNUALLY: 'annually'
    },

    /**
     * 产品类型枚举
     */
    PRODUCT_TYPES: {
        DNS: 'dns',
        CDN: 'cdn'
    }
};

export default subscriptionService;
